import { supabase } from './supabase';

export interface QuizChild {
  id: string;
  displayName: string;
  levelCode: string | null;
  preferredLanguage: string;
}

export interface AvailableQuiz {
  quizId: string;
  title: string;
  description: string | null;
  questionCount: number;
  levelName: string | null;
  subjectName: string | null;
  assignmentId: string | null;
  timeLimitSeconds: number | null;
  showFeedbackMode: string;
}

export interface QuizVisual {
  alt?: string;
  public_url?: string;
  render_status?: string;
}

export interface QuizContentItem {
  id: string;
  text?: string;
  visual?: QuizVisual;
}

export interface QuizQuestion {
  questionNumber: number;
  questionVersionId: string;
  questionType: string;
  language: string;
  prompt: string;
  options: QuizContentItem[];
  stimulus: Record<string, unknown>;
  marks: number;
}

export interface AnswerFeedback {
  isCorrect: boolean | null;
  marksAwarded: number | null;
  explanation: string | null;
}

export interface QuizAttemptResult {
  id: string;
  quizId: string;
  childId: string;
  status: string;
  score: number;
  maxScore: number;
  startedAt: string;
  submittedAt: string | null;
}

function throwIfError(error: { message: string } | null) {
  if (error) throw new Error(error.message);
}

export async function listAccessibleChildren(): Promise<QuizChild[]> {
  const { data, error } = await supabase
    .from('child_profiles')
    .select('id,display_name,level_code,preferred_language')
    .eq('status', 'active')
    .order('display_name');
  throwIfError(error);

  return (data ?? []).map((child) => ({
    id: child.id as string,
    displayName: child.display_name as string,
    levelCode: child.level_code as string | null,
    preferredLanguage: child.preferred_language as string,
  }));
}

export async function listAvailableQuizzes(childId: string): Promise<AvailableQuiz[]> {
  const { data, error } = await supabase.rpc('get_available_quizzes', { p_child_id: childId });
  throwIfError(error);

  return (data ?? []).map((quiz: Record<string, unknown>) => ({
    quizId: quiz.quiz_id as string,
    title: quiz.title as string,
    description: quiz.description as string | null,
    questionCount: Number(quiz.question_count ?? 0),
    levelName: quiz.level_name as string | null,
    subjectName: quiz.subject_name as string | null,
    assignmentId: quiz.assignment_id as string | null,
    timeLimitSeconds: quiz.time_limit_seconds === null ? null : Number(quiz.time_limit_seconds),
    showFeedbackMode: String(quiz.show_feedback_mode ?? 'after_submission'),
  }));
}

export async function startQuizAttempt(quiz: AvailableQuiz, childId: string): Promise<string> {
  const { data, error } = await supabase.rpc('start_quiz_attempt', {
    p_quiz_id: quiz.quizId,
    p_child_id: childId,
    p_assignment_id: quiz.assignmentId,
    p_client_attempt_id: crypto.randomUUID(),
  });
  throwIfError(error);
  if (!data) throw new Error('The quiz attempt could not be created.');
  return data as string;
}

export async function loadAttemptQuestions(attemptId: string): Promise<QuizQuestion[]> {
  const { data, error } = await supabase.rpc('get_quiz_attempt_questions', { p_attempt_id: attemptId });
  throwIfError(error);

  return (data ?? []).map((question: Record<string, unknown>) => ({
    questionNumber: Number(question.question_number),
    questionVersionId: question.question_version_id as string,
    questionType: question.question_type as string,
    language: question.language as string,
    prompt: question.prompt as string,
    options: Array.isArray(question.options) ? question.options as QuizContentItem[] : [],
    stimulus: question.stimulus && typeof question.stimulus === 'object'
      ? question.stimulus as Record<string, unknown>
      : {},
    marks: Number(question.marks ?? 1),
  }));
}

export async function submitQuizAnswer(
  attemptId: string,
  questionVersionId: string,
  response: Record<string, unknown>,
): Promise<AnswerFeedback> {
  const { data, error } = await supabase.rpc('submit_attempt_answer', {
    p_attempt_id: attemptId,
    p_question_version_id: questionVersionId,
    p_response: response,
  });
  throwIfError(error);
  const feedback = Array.isArray(data) ? data[0] : data;
  if (!feedback) return { isCorrect: null, marksAwarded: null, explanation: null };
  return {
    isCorrect: feedback.is_correct as boolean | null,
    marksAwarded: feedback.marks_awarded === null ? null : Number(feedback.marks_awarded),
    explanation: feedback.explanation as string | null,
  };
}

export async function finishQuizAttempt(attemptId: string): Promise<QuizAttemptResult> {
  const { data, error } = await supabase.rpc('finish_quiz_attempt', { p_attempt_id: attemptId });
  throwIfError(error);
  if (!data) throw new Error('The quiz result could not be created.');
  return {
    id: data.id as string,
    quizId: data.quiz_id as string,
    childId: data.child_id as string,
    status: data.status as string,
    score: Number(data.score ?? 0),
    maxScore: Number(data.max_score ?? 0),
    startedAt: data.started_at as string,
    submittedAt: data.submitted_at as string | null,
  };
}
